import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Package, Sparkles, TrendingUp, ArrowRight } from "lucide-react";
import { useBilling } from "@/hooks/useBilling";
import { getApiClient, getAppBridge } from "@/api/client";
import { redirectToUrl } from "@/utils/redirect";
import { getShopId } from "@/utils/shopId";
import { useToast } from "@/hooks/useToast";
import { toErrorMessage } from "@/utils/errorMessage";
import { getAddOns } from "@/utils/planLadder";
import { PLAN_PRICES, PLAN_PRODUCT_QUOTAS } from "@/utils/planUtils";

interface ExtraPackEligibility {
  eligible: boolean;
  reason?: string;
  currentUsage?: number;
  maxQuota?: number;
  percentUsed?: number;
}

interface Credits {
  available_credits: number;
  premium_credits: number;
}

export function ExtraPackOffer() {
  const { plan } = useBilling();
  const { showError } = useToast();
  const navigate = useNavigate();
  const [eligibility, setEligibility] = useState<ExtraPackEligibility | null>(null);
  const [credits, setCredits] = useState<Credits>({ available_credits: 0, premium_credits: 0 });
  const [loading, setLoading] = useState(true);
  const [purchasingProducts, setPurchasingProducts] = useState(false);
  const [purchasingPremium, setPurchasingPremium] = useState(false);

  useEffect(() => {
    const checkEligibility = async () => {
      try {
        const client = getApiClient();
        const shopId = getShopId();
        const qs = shopId ? `?shop_id=${shopId}` : '';
        const [eligibilityRes, creditsRes] = await Promise.all([
          client.get<ExtraPackEligibility>(`/api/billing/extra-pack/eligibility${qs}`),
          client.get<Credits>(`/api/billing/extra-pack/credits${qs}`),
        ]);
        setEligibility(eligibilityRes.data);
        setCredits(creditsRes.data);
      } catch (error: any) {
        console.error("Failed to check Extra Pack eligibility:", error);
      } finally {
        setLoading(false);
      }
    };

    checkEligibility();
  }, []);

  const handlePurchaseProducts = async () => {
    setPurchasingProducts(true);
    try {
      const client = getApiClient();
      const shopId = getShopId();
      const url = shopId
        ? `/api/billing/extra-pack/products?shop_id=${shopId}`
        : '/api/billing/extra-pack/products';
      const response = await client.post<{ checkout_url: string }>(url);
      const { checkout_url } = response.data ?? {};
      if (!checkout_url || typeof checkout_url !== 'string') {
        throw new Error('Checkout URL missing from server response');
      }
      redirectToUrl(checkout_url, getAppBridge());
    } catch (error: unknown) {
      showError(toErrorMessage(error, "Failed to create checkout."));
      setPurchasingProducts(false);
    }
  };

  const handlePurchasePremium = async () => {
    setPurchasingPremium(true);
    try {
      const client = getApiClient();
      const shopId = getShopId();
      const url = shopId
        ? `/api/billing/extra-pack/premium?shop_id=${shopId}`
        : '/api/billing/extra-pack/premium';
      const response = await client.post<{ checkout_url: string }>(url);
      const { checkout_url } = response.data ?? {};
      if (!checkout_url || typeof checkout_url !== 'string') {
        throw new Error('Checkout URL missing from server response');
      }
      redirectToUrl(checkout_url, getAppBridge());
    } catch (error: unknown) {
      showError(toErrorMessage(error, "Failed to create checkout."));
      setPurchasingPremium(false);
    }
  };

  if (loading) return null;

  const planCode = plan?.plan?.code;
  const premiumIncluded = plan?.usage?.premium_generations_included ?? 0;
  const premiumUsed = plan?.usage?.premium_generations_used ?? 0;
  const premiumRemaining = Math.max(0, premiumIncluded - premiumUsed);
  const showPremiumOffer = planCode === 'launch' || (planCode === 'growth' && premiumRemaining === 0);

  // Prices come from the server (`plan.add_ons`); the planLadder fallbacks
  // only apply when an older backend omits them.
  const addOns = getAddOns(plan);
  const productPackPrice = addOns.extra_products.price;
  const productPackCredits = addOns.extra_products.credits;
  const premiumPackPrice = addOns.premium_generation.price;
  const productPackPerUnit =
    productPackCredits > 0
      ? (productPackPrice / productPackCredits).toFixed(2)
      : productPackPrice.toFixed(2);
  const growthPerProduct = (
    PLAN_PRICES.growth.monthly / PLAN_PRODUCT_QUOTAS.growth
  ).toFixed(2);

  // Show active credits banners
  const hasProductCredits = credits.available_credits > 0;
  const hasPremiumCredits = credits.premium_credits > 0;

  if (hasProductCredits || hasPremiumCredits) {
    return (
      <div className="space-y-3">
        {hasProductCredits && (
          <div className="glass-panel border-emerald-100 bg-gradient-to-r from-emerald-50/60 to-teal-50/60">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 shadow-lg shadow-emerald-200">
                  <Package className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">Extra Product Pack Active</h3>
                  <p className="text-xs text-slate-600">
                    {credits.available_credits} extra {credits.available_credits === 1 ? "product" : "products"} remaining this month
                  </p>
                </div>
              </div>
              <div className="rounded-2xl border border-emerald-200 bg-white/80 px-4 py-2 text-sm font-semibold text-emerald-600">
                +{credits.available_credits} products
              </div>
            </div>
          </div>
        )}
        {hasPremiumCredits && (
          <div className="glass-panel border-indigo-100 bg-gradient-to-r from-indigo-50/60 to-sky-50/60">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-r from-indigo-500 to-sky-500 shadow-lg shadow-indigo-200">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">Premium Generation Upgrade Active</h3>
                  <p className="text-xs text-slate-600">
                    {credits.premium_credits} premium {credits.premium_credits === 1 ? "generation" : "generations"} remaining this month
                  </p>
                </div>
              </div>
              <div className="rounded-2xl border border-indigo-200 bg-white/80 px-4 py-2 text-sm font-semibold text-indigo-600">
                +{credits.premium_credits} premium
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Show offer if at ≥80% quota or premium offer is relevant
  if (!eligibility?.eligible && !showPremiumOffer) return null;

  return (
    <div className="space-y-3">
      {/* Quota wall (§11.2 presentation rule): the upgrade is shown FIRST and
          the one-time pack SECOND, with the per-unit comparison stated plainly
          so the subscription is the obvious better deal. `eligibility.eligible`
          is server-resolved and is false for free-tier shops, so they are never
          offered a pack. */}
      {eligibility?.eligible && (
        <>
          <div className="glass-panel border-indigo-100 bg-gradient-to-r from-indigo-50/60 to-sky-50/60">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-r from-indigo-500 to-sky-500 shadow-lg shadow-indigo-200">
                  <TrendingUp className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900">
                    You're at {eligibility.percentUsed}% of your monthly quota
                  </h3>
                  <p className="mt-1 text-sm text-slate-600">
                    {eligibility.currentUsage}/{eligibility.maxQuota} products used.
                    Which gets you to your goal faster? Moving up a plan gives you the
                    room permanently — and costs less per product than a one-time pack.
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate("/billing")}
                className="flex items-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-500 to-sky-500 px-6 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-200 transition hover:shadow-xl"
              >
                See plans
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="glass-panel border-amber-100 bg-gradient-to-r from-amber-50/60 to-orange-50/60">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 shadow-lg shadow-amber-200">
                  <Package className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900">
                    Or top up just this month
                  </h3>
                  <p className="mt-1 text-sm text-slate-600">
                    Add {productPackCredits} more products without changing your plan.
                    That's ${productPackPerUnit} per product, against ${growthPerProduct} per
                    product on The AR Sales Engine.
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-start gap-3 md:items-end">
                <div className="rounded-2xl border border-amber-200 bg-white/80 px-4 py-2">
                  <div className="text-xs font-medium uppercase tracking-wide text-amber-600">One-time add-on</div>
                  <div className="mt-1 flex items-baseline gap-1">
                    <span className="text-2xl font-bold text-slate-900">${productPackPrice}</span>
                    <span className="text-sm text-slate-500">for +{productPackCredits} products</span>
                  </div>
                </div>
                <button
                  onClick={handlePurchaseProducts}
                  disabled={purchasingProducts}
                  className="flex items-center gap-2 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-2 text-sm font-semibold text-white shadow-lg shadow-amber-200 transition hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {purchasingProducts ? "Redirecting..." : `Add ${productPackCredits} more products`}
                  {!purchasingProducts && <ArrowRight className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Premium Generation Upgrade offer */}
      {showPremiumOffer && (
        <div className="glass-panel border-indigo-100 bg-gradient-to-r from-indigo-50/60 to-sky-50/60">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-r from-indigo-500 to-sky-500 shadow-lg shadow-indigo-200">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-slate-900">
                  {planCode === 'growth'
                    ? "Need more Premium Generations this month?"
                    : "Upgrade a product to Premium Generation"}
                </h3>
                <p className="mt-1 text-sm text-slate-600">
                  {planCode === 'growth'
                    ? `You've used all ${premiumIncluded} included Premium Generations. Add 1 more for a product that needs better output.`
                    : "Use Multi-Angle Capture or Studio Render on a single product for better shape accuracy and detail."}
                </p>
              </div>
            </div>
            <div className="flex flex-col items-start gap-3 md:items-end">
              <div className="rounded-2xl border border-indigo-200 bg-white/80 px-4 py-2">
                <div className="text-xs font-medium uppercase tracking-wide text-indigo-600">Per product</div>
                <div className="mt-1 flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-slate-900">${premiumPackPrice}</span>
                  <span className="text-sm text-slate-500">/ product</span>
                </div>
              </div>
              <button
                onClick={handlePurchasePremium}
                disabled={purchasingPremium}
                className="flex items-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-500 to-sky-500 px-6 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-200 transition hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60"
              >
                {purchasingPremium ? "Redirecting..." : "Upgrade this product"}
                {!purchasingPremium && <ArrowRight className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
